import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { SESSION_QUERY_KEY, type SessionApiResponse, type SessionTenantSummary } from '@/lib/api/session';
import { apiFetch } from '@/lib/api/requests';
import {
  resolveClientTenantRole,
  resolveRolePermissions as resolveTenantRolePermissions,
} from '@/lib/auth/roles';
import { useTenantStore } from '@/stores/tenantStore';

type SessionStateCode = 'OK' | 'TENANT_UNASSIGNED' | 'UNAUTHENTICATED';

export interface SessionQueryResult {
  authenticated: boolean;
  user: SessionApiResponse['user'] | null;
  tenant: SessionApiResponse['tenant'] | null;
  state: SessionStateCode;
  onboardingStatus: 'pending' | 'completed';
  permissions: string[];
  tenants: SessionTenantSummary[];
}

interface SessionEnvelope {
  ok: boolean;
  session: {
    authenticated: boolean;
    user?: SessionApiResponse['user'] | null;
    tenant?: SessionApiResponse['tenant'] | null;
    state?: SessionStateCode;
    onboardingStatus?: 'pending' | 'completed';
    permissions?: string[];
    tenants?: SessionTenantSummary[];
  };
}

const BASE_SESSION_REFETCH_INTERVAL_MS = 60 * 1000;
const BACKOFF_STEP_MS = 1_000;
const BACKOFF_MAX_MS = 5_000;
const DEFAULT_SESSION: SessionQueryResult = {
  authenticated: false,
  user: null,
  tenant: null,
  state: 'OK',
  onboardingStatus: 'completed',
  permissions: [],
  tenants: [],
};

const formatRole = (role: string | null | undefined, hasTenant: boolean): string => {
  const resolved = resolveClientTenantRole(role);
  if (resolved) {
    return resolved;
  }
  return hasTenant ? 'SUPPORT' : 'SUPPORT';
};

const ensurePermissions = (
  permissions: unknown,
  role: string | null | undefined,
  hasTenant: boolean,
): string[] => {
  const normalized = new Set<string>();

  if (Array.isArray(permissions)) {
    permissions.forEach((permission) => {
      if (typeof permission === 'string' && permission.length > 0) {
        normalized.add(permission);
      }
    });
  }

  if (hasTenant) {
    resolveTenantRolePermissions(role).forEach((permission) => normalized.add(permission));
  }

  return Array.from(normalized);
};

function normalizeSessionPayload(raw: unknown): SessionQueryResult {
  if (raw && typeof raw === 'object') {
    const envelope = raw as SessionEnvelope;

    if (envelope.session && typeof envelope.session === 'object') {
      const session = envelope.session;
      const authenticated = Boolean(session.authenticated);
      const tenant = session.tenant ?? null;
      const hasTenant = Boolean(tenant);
      const state =
        (session.state as SessionStateCode | undefined) ??
        (authenticated && tenant ? 'OK' : 'UNAUTHENTICATED');
      const onboardingStatus: 'pending' | 'completed' =
        session.onboardingStatus === 'pending' ? 'pending' : 'completed';

      const normalizedRole = session.user
        ? formatRole(session.user.role ?? null, hasTenant)
        : 'SUPPORT';

      const normalizedUser = session.user
        ? {
            ...session.user,
            role: normalizedRole,
          }
        : null;

      const permissions = ensurePermissions(session.permissions, normalizedRole, hasTenant);
      const tenants = Array.isArray(session.tenants)
        ? session.tenants.map((tenantSummary) => ({
            id: tenantSummary.id,
            slug: tenantSummary.slug,
            name: tenantSummary.name ?? tenantSummary.slug,
            role: tenantSummary.role ?? null,
            status: tenantSummary.status ?? null,
            lastUsedAt: tenantSummary.lastUsedAt ?? null,
            isDemo: tenantSummary.isDemo ?? false,
          }))
        : [];

      return {
        authenticated,
        user: normalizedUser,
        tenant,
        state,
        onboardingStatus,
        permissions,
        tenants,
      };
    }

    if ('authenticated' in envelope) {
      const legacy = envelope as SessionApiResponse;
      const authenticated = Boolean(legacy.authenticated);
      const tenant = legacy.tenant ?? null;
      const hasTenant = Boolean(tenant);
      const state: SessionStateCode = authenticated && tenant ? 'OK' : 'UNAUTHENTICATED';
      const onboardingStatus: 'pending' | 'completed' =
        (legacy as SessionEnvelope['session'])?.onboardingStatus === 'pending' ? 'pending' : 'completed';
      const normalizedRole = legacy.user
        ? formatRole(legacy.user.role ?? null, hasTenant)
        : 'SUPPORT';
      const normalizedUser = legacy.user
        ? {
            ...legacy.user,
            role: normalizedRole,
          }
        : null;
      const permissions = ensurePermissions(undefined, normalizedRole, hasTenant);
      const tenants = Array.isArray((legacy as SessionEnvelope['session'])?.tenants)
        ? ((legacy as SessionEnvelope['session'])?.tenants ?? []).map((tenantSummary) => ({
            id: tenantSummary.id,
            slug: tenantSummary.slug,
            name: tenantSummary.name ?? tenantSummary.slug,
            role: tenantSummary.role ?? null,
            status: tenantSummary.status ?? null,
            lastUsedAt: tenantSummary.lastUsedAt ?? null,
            isDemo: tenantSummary.isDemo ?? false,
          }))
        : [];

      return {
        authenticated,
        user: normalizedUser,
        tenant,
        state,
        onboardingStatus,
        permissions,
        tenants,
      };
    }
  }

  return DEFAULT_SESSION;
}


class RateLimitedSessionError extends Error {
  retryAfterMs?: number;

  constructor(message: string, retryAfterMs?: number) {
    super(message);
    this.name = 'RateLimitedSessionError';
    this.retryAfterMs = retryAfterMs;
  }
}

const SESSION_REQUEST_PATH = '/session';

const requestSession = async (signal?: AbortSignal): Promise<SessionQueryResult> => {
  const response = await apiFetch(SESSION_REQUEST_PATH, {
    headers: {
      Accept: 'application/json',
    },
    signal,
  });

  if (response.status === 429) {
    const retryAfterHeader = response.headers.get('Retry-After');
    const retryAfterSeconds =
      typeof retryAfterHeader === 'string' ? Number.parseInt(retryAfterHeader, 10) : NaN;
    const retryAfterMs = Number.isFinite(retryAfterSeconds)
      ? Math.max(retryAfterSeconds, 1) * 1000
      : undefined;
    throw new RateLimitedSessionError('Session endpoint rate limited', retryAfterMs);
  }

  if (!response.ok) {
    throw new Error(`[useSession] session endpoint returned status ${response.status}`);
  }

  const json = await response.json();
  return normalizeSessionPayload(json);
};

export type UseSessionResult = UseQueryResult<SessionQueryResult, Error> & {
  data: SessionQueryResult;
  loading: boolean;
  authenticated: boolean;
  user: SessionQueryResult['user'];
  tenant: SessionQueryResult['tenant'];
  onboardingStatus: SessionQueryResult['onboardingStatus'];
  permissions: string[];
  tenants: SessionQueryResult['tenants'];
  lastError: Error | null;
  triggerRefetch: () => Promise<SessionQueryResult>;
};

export const useSession = (): UseSessionResult => {
  const setActiveTenant = useTenantStore((state) => state.setActiveTenant);
  const clearTenant = useTenantStore((state) => state.clearTenant);
  const setTenantRoster = useTenantStore((state) => state.setTenantRoster);
  const resolveActiveTenant = useTenantStore((state) => state.resolveActiveTenant);
  const queryClient = useQueryClient();
  const lastTenantIdRef = useRef<string | null>(null);
  const backoffRef = useRef(0);
  const [lastError, setLastError] = useState<Error | null>(null);

  const query = useQuery<SessionQueryResult, Error>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => requestSession(signal),
    staleTime: BASE_SESSION_REFETCH_INTERVAL_MS,
    refetchInterval: (data) => {
      if (!data) {
        backoffRef.current = 0;
        return BASE_SESSION_REFETCH_INTERVAL_MS;
      }

      if (data.state === 'OK') {
        backoffRef.current = 0;
        return BASE_SESSION_REFETCH_INTERVAL_MS;
      }

      if (data.state === 'TENANT_UNASSIGNED') {
        return false;
      }

      const nextDelay =
        backoffRef.current === 0
          ? BACKOFF_STEP_MS
          : Math.min(BACKOFF_MAX_MS, backoffRef.current + BACKOFF_STEP_MS);

      backoffRef.current = nextDelay;
      return nextDelay;
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error instanceof RateLimitedSessionError) {
        return failureCount < 3;
      }
      return failureCount < 2;
    },
    retryDelay: (failureCount, error) => {
      if (error instanceof RateLimitedSessionError) {
        return error.retryAfterMs ?? Math.min(BACKOFF_MAX_MS, (failureCount + 1) * BACKOFF_STEP_MS);
      }
      return Math.min(BACKOFF_MAX_MS, (failureCount + 1) * BACKOFF_STEP_MS);
    },
    onSuccess: () => {
      setLastError(null);
    },
    onError: (error) => {
      setLastError(error);
      if (!queryClient.getQueryData(SESSION_QUERY_KEY)) {
        queryClient.setQueryData(SESSION_QUERY_KEY, DEFAULT_SESSION);
      }
      if (import.meta.env.DEV) {
        console.warn('[useSession] session request failed:', error);
      }
    },
  });

  const sessionData = useMemo(() => query.data ?? DEFAULT_SESSION, [query.data]);

  useEffect(() => {
    setTenantRoster(sessionData.tenants ?? []);
  }, [sessionData.tenants, setTenantRoster]);

  useEffect(() => {
    const data = sessionData;

    if (!data) {
      return;
    }

    if (data.state === 'OK' && data.authenticated && data.tenant) {
      if (lastTenantIdRef.current !== data.tenant.id) {
        setActiveTenant(
          data.tenant.id,
          data.tenant.slug,
          data.tenant.name ?? data.tenant.slug,
          false,
        );
        lastTenantIdRef.current = data.tenant.id;
      }
      return;
    }

    if ((data.tenants?.length ?? 0) > 0) {
      void resolveActiveTenant();
      return;
    }

    if (lastTenantIdRef.current) {
      clearTenant();
      lastTenantIdRef.current = null;
    }
  }, [sessionData, setActiveTenant, clearTenant, resolveActiveTenant]);

  const refetchRef = useRef(query.refetch);

  useEffect(() => {
    refetchRef.current = query.refetch;
  }, [query.refetch]);

  const triggerRefetch = useCallback(async () => {
    backoffRef.current = 0;
    setLastError(null);
    const result = await refetchRef.current();
    return result.data ?? DEFAULT_SESSION;
  }, []);

  const loading = query.isLoading;
  const authenticated = sessionData.authenticated;

  return {
    ...query,
    data: sessionData,
    loading,
    authenticated,
    user: sessionData.user,
    tenant: sessionData.tenant,
    onboardingStatus: sessionData.onboardingStatus,
    permissions: sessionData.permissions,
    tenants: sessionData.tenants,
    lastError,
    triggerRefetch,
  } as UseSessionResult;
};

